import { useMemo } from 'react';
import { HStack, IconHistory, Text } from '@direct-frontend/components';
import { type Nullable } from '@direct-frontend/stdlib';
import { getOrderTrackingInfo } from '@/entities/order';
import { OrderDeliveryMethods, type ServiceType } from '@/shared/api/order';
import { type IntervalFieldValue } from '@/shared/components/IntervalInput';
import { ServiceTypeIcon } from '@/shared/components/ServiceTypeIcon';
import { Trigger, type TriggerProps } from '@/shared/components/Trigger';
import { formatTimestampInterval } from '@/shared/lib/datetime';
import { DeliveryIntervalPicker } from './components/DeliveryIntervalPicker';
import { type OzonDeliveryInterval } from '../../lib';

type Interval = {
    from: number;
    to: number;
    serviceType: ServiceType;
    price: Nullable<string>;
    currency: Nullable<string>;
};

type DeliveryIntervalInputProps = TriggerProps & {
    value: IntervalFieldValue;
    onChange?: (value: IntervalFieldValue) => void;
    validationState?: 'valid' | 'invalid';
    isLoading?: boolean;
    intervals: Interval[];
    isShowServiceLogo?: boolean;
    deliveryMethod: Nullable<string>;
    ozonDeliveryInterval: OzonDeliveryInterval;
};

export const DeliveryIntervalInput = (props: DeliveryIntervalInputProps) => {
    const {
        value,
        onChange,
        intervals,
        isShowServiceLogo,
        deliveryMethod,
        isDisabled,
        rightContent,
        caption,
        ozonDeliveryInterval,
        ...other
    } = props;

    const time = useMemo(
        () =>
            value?.type === 'interval'
                ? formatTimestampInterval(value, { showOnlyDate: deliveryMethod === OrderDeliveryMethods.PICKUP_POINT })
                : '',
        [deliveryMethod, value]
    );

    const title = useMemo(() => {
        if (time) {
            return time;
        }

        return '';
    }, [time]);

    const serviceLogo =
        isShowServiceLogo && value.type === 'interval' && value.serviceType ? (
            <ServiceTypeIcon serviceType={value.serviceType} size="24" />
        ) : undefined;

    const trackingInfo =
        isShowServiceLogo && value.type === 'interval' && value.serviceType
            ? getOrderTrackingInfo(value.serviceType)
            : '';

    return (
        <DeliveryIntervalPicker
            value={value}
            onChange={onChange}
            intervals={intervals}
            deliveryMethod={deliveryMethod}
            ozonDeliveryInterval={ozonDeliveryInterval}
        >
            {({ onOpenModal }) => (
                <Trigger
                    {...other}
                    iconLeft={<IconHistory size="24" />}
                    iconRight={rightContent ?? serviceLogo}
                    caption={caption ?? trackingInfo}
                    isDisabled={isDisabled}
                    onClick={onOpenModal}
                >
                    <HStack as="span" alignItems="baseline">
                        <Text role={isDisabled ? 'supplementary' : 'main'} size="mc">
                            {title}
                        </Text>
                    </HStack>
                </Trigger>
            )}
        </DeliveryIntervalPicker>
    );
};
